import * as R from 'ramda'
import { LOCATION_CHANGE } from 'connected-react-router'
import { isActionType } from 'helpers/ramda-ext/actions'
import screenSize from 'constants/screenSize'
import { DEFAULT_COUNTRY, DEFAULT_CULTURE } from 'constants/locale'
import { DEFAULT_THEME_NAME } from 'constants/theme'
import { getOpeCodeInPathname, isOneOfOldRoutes } from 'helpers/PathHelper'
import * as Actions from 'store/actions'
import { RegisteredAction } from 'store/actions/index.types'
import { InitialState, State } from './application.types'

const initialState: Readonly<InitialState> = {
  device: {
    type: 'web'
  },
  env: '',
  features: {
    backToHome: false,
    displayCallPrices: false,
    displayQuoteTOS: false,
    hotjar: false,
    lastViewedProduct: false,
    oneTrust: false,
    orchCheckoutV3: false,
    paymentBanner: false,
    reviews: false,
    shouldDoubleThePrice: false,
    shouldHideReferencePrice: false,
    shouldShowNavigationRedirectionButton: false,
    toggleCheapestPrice: false,
    viewAllOffers: false,
    viewProductsMap: false
  },
  isPreview: false,
  isProductsMapOpen: false,
  isSaleChanging: false,
  isSaleFetched: false,
  locale: {
    countryCode: DEFAULT_COUNTRY,
    cultureCode: DEFAULT_CULTURE,
    hasMomentLocaleBeenSet: false
  },
  marketId: undefined,
  operationCode: '',
  operationId: undefined,
  partner: '',
  paths: {
    mediaPath: 'test'
  },
  referer: undefined,
  saleKey: '',
  screenSize,
  template: '',
  theme: undefined
}

// eslint-disable-next-line @typescript-eslint/default-param-last
export default (state: State = initialState, action: RegisteredAction): State => R.cond([
  [
    isActionType(Actions.application.WINDOW_RESIZE),
    R.always(R.assoc('screenSize', R.path(['payload', 'screenSize'], action), state))
  ],
  [
    isActionType(Actions.application.SET_DEVICE),
    act => R.assocPath(['device', 'type'], act.payload.device, state)
  ], [
    isActionType(Actions.version.VERSION_CHANGED),
    R.always(R.assoc('isSaleFetched', false, state))
  ], [
    isActionType(Actions.resources.RESOURCE_FETCHED),
    R.ifElse(
      R.always(R.equals('sale', R.path(['payload', 'type'], action))),
      R.always(R.assoc('isSaleFetched', true, state)),
      R.always(state)
    )
  ], [
    isActionType(Actions.member.MEMBER_INFO_FETCHED),
    () => R.pipe(
      R.assoc(
        'isPreview',
        R.pathEq(['payload', 'response', 'member', 'memberId'], '-1', action)
      ),
      R.assoc('features', R.path(['payload', 'response', 'features'], action)),
      R.assoc('operationId', R.path(['payload', 'response', 'operationId'], action)),
      R.assoc('saleKey', R.path(['payload', 'response', 'eventKey'], action)),
      R.assocPath(['paths', 'mediaPath'], R.path(['payload', 'response', 'mediaBaseUrl'], action)),
      R.assoc('template', R.path(['payload', 'response', 'template'], action)),
      R.assoc(
        'theme',
        R.pipe(
          R.path(['payload', 'response', 'theme']),
          R.defaultTo(DEFAULT_THEME_NAME),
          R.toUpper
        )(action)
      ),
      R.assoc('partner', R.path(['payload', 'response', 'partner'], action)),
      R.assoc(
        'marketId',
        R.path(['payload', 'response', 'market'])(action)
      ),
      R.assoc(
        'operationCode',
        R.pipe(
          R.path(['payload', 'response', 'operationCode']),
          R.defaultTo(''),
          R.toUpper
        )(action)
      ),
      R.assocPath(
        ['locale', 'countryCode'],
        R.pipe(
          R.path(['payload', 'response', 'locale', 'countryCode']),
          R.defaultTo(DEFAULT_COUNTRY),
          R.toUpper
        )(action)
      ),
      R.assocPath(
        ['locale', 'cultureCode'],
        R.pipe(
          R.path(['payload', 'response', 'locale', 'cultureCode']),
          R.defaultTo(DEFAULT_CULTURE),
          R.toLower
        )(action)
      ),
      R.assoc(
        'referer',
        R.path(['payload', 'response', 'referer'], action)
      )
    )(state)
  ], [
    isActionType(Actions.theme.THEME_SELECTED),
    act => R.assoc('theme', act.payload.themeName, state)
  ], [
    isActionType(Actions.application.SALE_CHANGES),
    R.always({
      ...initialState,
      device: state.device,
      locale: state.locale,
      marketId: state.marketId,
      partner: state.partner,
      screenSize: state.screenSize,
      theme: state.theme
    })
  ], [
    isActionType(LOCATION_CHANGE),
    act => {
      const isOldRoute = (pathname: string): boolean => (
        pathname !== undefined &&
        isOneOfOldRoutes(pathname) === true
      )
      const isOperationCodeChange = (operationCode: string): boolean => (
        Boolean(state.operationCode) &&
        state.operationCode !== operationCode
      )

      return R.pipe(
        R.path(['payload', 'location', 'pathname']),
        R.ifElse(isOldRoute,
          R.always(state),
          R.pipe(
            getOpeCodeInPathname,
            (code: string): State => isOperationCodeChange(code) ? R.assoc('isSaleChanging', true, state) : state
          )
        )
      )(act)
    }
  ], [
    isActionType(Actions.application.SET_MOMENT_LOCALE),
    _act => R.assocPath(['locale', 'hasMomentLocaleBeenSet'], true, state)
  ], [
    isActionType(Actions.application.TOGGLE_PRODUCTS_MAP),
    act => R.assoc('isProductsMapOpen', act.payload.isOpen, state)
  ], [
    R.T,
    R.always(state)
  ]
])(action)
